import { getDisplayNameByKey, orderableItemValues } from "@sellar-io/common";
import { BadgePurple, Button, Icon } from "@sellar-io/components";
import { useState } from "react";
import { SpinnerLegacy } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { useProductTemplateList } from "../../../../hooks/api/v2/productTemplate/useProductTemplateList";
import { ProductContainerTemplateForm } from "./ProductContainerTemplateForm";
import {
  containerCategories,
  containerIcons,
  getContainerOptions,
} from "./productContainerTemplateOptions";
import { EmptyStateCard } from "../../../../components/emptyStateCard/EmptyStateCard";

export const ProductContainerTemplateSetup = ({
  onCreateProduct,
}: {
  onCreateProduct: () => void;
}) => {
  const [showContainerForm, setShowContainerForm] = useState(false);
  const templatesQuery = useProductTemplateList({ limit: 100 });
  const templates = templatesQuery.data?.pages.flat() ?? [];

  if (templatesQuery.isPending) return <SpinnerLegacy />;

  if (templatesQuery.isError) {
    return (
      <Alert
        type="error"
        title="Unable to load your containers"
        message="Refresh the page to try again."
      />
    );
  }

  if (!showContainerForm && templates.length > 0) {
    return (
      <EmptyStateCard
        icon="PACKAGE_VARIANT_CLOSED"
        badge={<BadgePurple icon="SELLAR_NEW">Product setup</BadgePurple>}
        title="Add your products"
        description={
          <p className="text-base">
            Your container formats are ready and listed below. Add your first
            product and use your created container types to list the formats you
            sell it in
          </p>
        }
      >
        <div className="flex w-full flex-wrap gap-3">
          {templates.map((template) => {
            const category = containerCategories.find(({ key }) =>
              getContainerOptions(key).some(
                ({ key: type }) => type === template.containerType,
              ),
            );
            const type = getDisplayNameByKey(
              orderableItemValues,
              template.containerType,
            );
            return (
              <span
                key={template.id}
                className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 font-semibold text-emerald-700"
              >
                <Icon icon={containerIcons[category?.key ?? "other"]} />
                {category?.label ?? type} ·{" "}
                {category &&
                  !["can", "bottle", "bagInBox"].includes(category.key) &&
                  `${type} · `}
                {template.packQuantity > 1 && `${template.packQuantity} × `}
                {template.volume}
                {template.displayUnit}
              </span>
            );
          })}
        </div>
        <div className="flex w-full flex-wrap justify-end gap-3">
          <Button color="secondary" onClick={() => setShowContainerForm(true)}>
            Add more containers
          </Button>
          <Button onClick={onCreateProduct}>Add my first product</Button>
        </div>
      </EmptyStateCard>
    );
  }

  return (
    <ProductContainerTemplateForm
      templates={templates}
      onStarted={() => setShowContainerForm(true)}
      onFinished={() => setShowContainerForm(false)}
    />
  );
};
